import React, { forwardRef } from 'react';

export interface DetailsProps extends React.DetailsHTMLAttributes<HTMLDetailsElement> {
  children: React.ReactNode;
}

export const Details = forwardRef<HTMLDetailsElement, DetailsProps>(
  ({ children, className = '', ...props }, ref) => {
    return (
      <details
        ref={ref}
        className={`relative ${className}`}
        {...props}
      >
        {children}
      </details>
    );
  }
);

Details.displayName = 'Details';

export interface SummaryProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactNode;
  disabled?: boolean;
}

export const Summary: React.FC<SummaryProps> = ({
  children,
  className = '',
  disabled = false,
  ...props
}) => {
  return (
    <summary
      className={`
        list-none cursor-pointer focus:outline-none
        [&::-webkit-details-marker]:hidden
        flex items-center justify-center w-8 h-8 
        bg-white border border-solid border-gray-300
        relative p-0 shadow-[0_12px_16px_rgba(254,80,0,0.1)]
        rounded-[4px]
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        ${className}
      `}
      {...props}
    >
      {children}
    </summary>
  );
};

export interface MenuProps {
  children: React.ReactNode;
  className?: string;
  position?: 'right' | 'left';
}

export const Menu: React.FC<MenuProps> = ({
  children,
  className = '',
  position = 'right'
}) => {
  const positionClasses = position === 'left' ? '!left-0 !right-auto' : '';

  return (
    <div
      className={`
        absolute top-full right-0 mt-[0.4rem] bg-white border border-[#ccc] z-[1001] 
        p-[10px] min-w-[150px] rounded-[0.5rem]
        ${positionClasses}
        ${className}
      `}
    >
      {children}
    </div>
  );
};

export interface SectionTitleProps {
  children: React.ReactNode;
  className?: string;
}

export const SectionTitle: React.FC<SectionTitleProps> = ({
  children,
  className = ''
}) => {
  return (
    <div
      className={`
        font-bold py-2 px-4 text-[var(--ringil3-content-primary)] text-[0.9rem] 
        border-b border-[var(--ringil3-content-quaternary)] mb-2
        ${className}
      `}
    >
      {children}
    </div>
  );
};

export interface MenuListProps {
  children: React.ReactNode;
  className?: string;
}

export const MenuList: React.FC<MenuListProps> = ({
  children,
  className = ''
}) => {
  return (
    <ul
      className={`
        list-none p-0 m-0 mb-[15px] last:mb-0
        ${className}
      `}
    >
      {children}
    </ul>
  );
};

export interface MenuItemProps {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}

export const MenuItem: React.FC<MenuItemProps> = ({
  children,
  onClick,
  disabled = false,
  className = ''
}) => {
  return (
    <li
      className={`
        cursor-pointer flex items-center gap-2 p-4 whitespace-nowrap 
        border-b border-[var(--content-quaternary)] last:border-b-0
        hover:bg-[var(--ringil-base-quaternarycolor)]
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        ${className}
      `}
      onClick={disabled ? undefined : onClick}
    >
      {children}
    </li>
  );
};
